import LatLon from "geodesy/latlon-ellipsoidal-vincenty";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps } from "react-router";
import ClientSplashScreen from "../Components/ClientSplashScreen";
import useInterval from "../../Shared/useInterval";
import {
    getAccessInfoForQuickCode,
    getSessionInfo,
    getActiveObservers,
    heartbeatPing,
    setCurrentPosition,
    endClientRegistration,
    getActiveMentee,
    endPreviousClientRegistration,
} from "../redux/actions/currentClientActions";
import { updateCanLeave } from "../redux/actions/exitActions";
import { ApplicationData, ClientPhase } from "../types";
import IntroPhases from "./IntroPhases";
import ContinueLater from "./ContinueLater";
import InvalidCode from "./InvalidCode";
import SessionPage from "./Session/SessionPage";
import moment from "moment";
import "moment/min/locales";
import { DateTime } from "luxon";

const mapState = function ({
    order,
    accessInfo,
    permissions,
    currentPosition,
    localizations,
    invalidCode,
    canLeave,
}: ApplicationData) {
    return {
        order,
        accessInfo,
        permissions,
        currentPosition,
        localizations,
        invalidCode,
        canLeave,
    };
};

const mapDispatch = {
    getSessionInfo,
    getActiveObservers,
    heartbeatPing,
    getAccessInfoForQuickCode,
    setCurrentPosition,
    updateCanLeave,
    endClientRegistration,
    getActiveMentee,
    endPreviousClientRegistration,
};
const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type PropTypes = PropsFromRedux & RouteComponentProps & {};

const HEARTBEAT_INTERVAL = 1000 * 8;

function ClientApp({
    getSessionInfo,
    order,
    getActiveObservers,
    heartbeatPing,
    location,
    accessInfo,
    getAccessInfoForQuickCode,
    permissions,
    setCurrentPosition,
    currentPosition,
    localizations,
    invalidCode,
    canLeave,
    updateCanLeave,
    endClientRegistration,
    getActiveMentee,
    endPreviousClientRegistration,
}: PropTypes) {
    /********************************
     *
     *  FOR DEVELOPMENT PURPOSES YOU CAN OFTEN SET THIS TO A SPECIFIC STEP.
     *  ALTHOUGH IT MAY BREAK DEPENDING ON IF OPTIONS ARE ALREADY SET.  USE WITH CARE.
     *
     *******************************/

    const [phase, setPhase] = useState(ClientPhase.Welcome);
    const [isLoading, setIsLoading] = useState(true);
    const [positionCollection, updatePositionCollection] = useState<Position[]>([]);
    const queryObj = qs.parse(location.search, { ignoreQueryPrefix: true });

    useEffect(() => {
        if (!accessInfo) {
            getAccessInfoForQuickCode(queryObj.quickCode as string);
        } else {
            getSessionInfo();
        }

        endPreviousClientRegistration();

        getActiveMentee();
    }, [accessInfo]);

    const catchLeave = (e: BeforeUnloadEvent) => {
        endClientRegistration();

        if (!canLeave) {
            // Cancel the event
            e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
            // Chrome requires returnValue to be set
            e.returnValue = localizations?.youHaveAnActiveSession;
            return e.returnValue;
        }
        return;
    };

    //Need to rebind as the value changes or use ref vs State here
    useEffect(() => {
        window.addEventListener("beforeunload", catchLeave);
        return () => window.removeEventListener("beforeunload", catchLeave);
    }, [canLeave]);

    //Jump to review if you have completion date and are not set
    useEffect(() => {
        let phases = [ClientPhase.SessionEnded, ClientPhase.Review, ClientPhase.ProvideFeedback];
        if (order && order.clientSessionEnded && !phases.find((p) => p === phase)) {
            if (!canLeave) {
                updateCanLeave(true);
            }

            if (phase === ClientPhase.GuideStreamOnline) {
                endClientRegistration();
                setPhase(ClientPhase.SessionEnded);
            } else {
                setPhase(ClientPhase.Review);
            }
        }
        if (order) {
            moment.locale(order.cultureCode);
            const timeout = setTimeout(() => {
                setIsLoading(false);
            }, 7000);

            return () => clearTimeout(timeout);
        }
    }, [order]);

    //GEO-LOCATION WATCHER
    useEffect(() => {
        const geo = navigator.geolocation;

        let watcher: number | null = null;
        if (permissions.haveLocationAccess === "granted") {
            watcher = geo.watchPosition(
                (position: Position) => {
                    if (
                        position.coords &&
                        (currentPosition === null ||
                            new LatLon(currentPosition.coords.latitude, currentPosition.coords.longitude).distanceTo(
                                new LatLon(position.coords.latitude, position.coords.longitude)
                            ) > 0.5)
                    ) {
                        let pos = {
                            timestamp: position.timestamp,
                            coords: {
                                accuracy: position.coords.accuracy,
                                altitude: position.coords.altitude,
                                altitudeAccuracy: position.coords.altitudeAccuracy,
                                heading: position.coords.heading,
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                                speed: position.coords.speed,
                            },
                        };

                        setCurrentPosition(pos);
                        let positions = [...positionCollection];
                        positions.push(pos);
                        updatePositionCollection(positions);
                    }
                },
                () => {
                    console.log("error setting up geo watch");
                },
                {
                    enableHighAccuracy: true,
                }
            );
        }
        return () => {
            watcher && geo.clearWatch(watcher);
        };
    }, [permissions.haveLocationAccess]);

    useInterval(() => {
        var currentPositions = positionCollection;
        heartbeatPing(currentPositions);
        updatePositionCollection([]);
    }, HEARTBEAT_INTERVAL);

    const OBSERVER_INTERVAL = 1000 * 10;

    useInterval(() => {
        if (phase === ClientPhase.GuideStreamOnline) {
            getActiveObservers();
        }
    }, OBSERVER_INTERVAL);

    let expired = !!order && !!order.expirationDate && order.expirationDate < DateTime.utc();

    if (order && localizations && !expired && !isLoading) {
        if (phase === ClientPhase.GuideStreamOnline) {
            return <SessionPage setPhase={setPhase} localizations={localizations}></SessionPage>;
        } else if (phase === ClientPhase.SessionEndedTemporarily) {
            return (
                <ContinueLater
                    localizations={localizations}
                    quickCode={order.quickCode}
                    provideFeedback={() => setPhase(ClientPhase.ProvideFeedback)}
                ></ContinueLater>
            );
        } else if (phase === ClientPhase.Removed) {
            return <h1>Removed from session</h1>;
        } else {
            return <IntroPhases phase={phase} setPhase={setPhase}></IntroPhases>;
        }
    } else if (invalidCode || expired) {
        return <InvalidCode></InvalidCode>;
    } else {
        return <ClientSplashScreen localizations={localizations} order={order} />;
    }
}
export default connector(ClientApp);
